import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { actions, FullInvoice, invoiceConfigByCode, invoiceStatusConfig } from 'entities';

import { DropdownMenu, DropdownMenuItem } from 'components/Blocks';
import { CircleXIcon } from 'components/Icons';

import { useUser } from 'hooks/user';
import { useJustification } from 'providers/Justification';
import { useConfirmation } from 'providers/Confirmation';

interface InvoiceMenuProps {
  children?: never;
  invoice: FullInvoice;
  updateInvoice: (invoice: FullInvoice) => void;
}

export function InvoiceMenu({ invoice, updateInvoice }: InvoiceMenuProps) {
  const { t } = useLang();
  const user = useUser();

  const { requestConfirmation } = useConfirmation();
  const { openJustificationModal } = useJustification();

  const isLeader = user.type === 'LEADER';

  const cancelInvoice = actions.invoice.cancelInvoice.use();
  const requestInvoiceCancelation = actions.invoice.requestInvoiceCancelation.use();

  const cancel = useCallback(() => {
    openJustificationModal({
      text: t('please-provide-a-reason-to-this-cancelation'),

      onConfirm: async (justification) =>
        requestConfirmation({
          title: t('cancel-invoice-?')!,

          type: 'irreversible',

          text: t('this-cancelation-will-be-final'),

          onConfirm: async () => {
            const payload = {
              invoice: invoice.id,
              justification,
            };

            const success = await (isLeader
              ? cancelInvoice.execute(payload)
              : requestInvoiceCancelation.execute(payload));

            if (!success) return;

            updateInvoice({
              ...invoice,

              ...(isLeader
                ? {
                    status: invoiceStatusConfig.canceled.code,
                  }
                : {
                    cancelationRequestId: (success as { requestId: string }).requestId,
                  }),
            });
          },
        }),
    });
  }, [
    isLeader,
    invoice,
    requestConfirmation,
    cancelInvoice,
    requestInvoiceCancelation,
    t,
    updateInvoice,
    openJustificationModal,
  ]);

  const canCancel =
    invoiceConfigByCode[invoice.status]?.canCancel &&
    (isLeader ? true : !invoice.cancelationRequestId);

  if (!canCancel) return null;

  return (
    <DropdownMenu align="start">
      <DropdownMenuItem Icon={CircleXIcon} onClick={cancel}>
        {isLeader ? t('cancel') : t('request-cancelation')}
      </DropdownMenuItem>
    </DropdownMenu>
  );
}
